import { DroppableZone } from "@/lib/dnd/components/DroppableZone";
import { useQuestionHandlers } from "@/lib/dnd/hooks/useQuestionHandlers";
import { useLocalImageUpload } from "@/lib/hooks/slides/editor/useLocalImageUpload";
import { useAppDispatch } from "@/store/hooks";
import { updateElementProps } from "@/store/slideEditorSlice";
import { CANVAS_DEFAULT_FONT_FAMILY, DragMatchingTextStyles } from "@ai-teach/canvas-styles";
import type { DragMatchingTextElement } from "@ai-teach/types";
import { IconButton } from "@ai-teach/ui-web";
import { AiFillPicture } from "react-icons/ai";
import { HiPlus } from "react-icons/hi";
import { MdCheckBox, MdCheckBoxOutlineBlank } from "react-icons/md";
import { DeleteImageButton } from "./DeleteImageButton";
import { gradeChoice, gradedFillStyle } from "./gradedStyles";

interface DragMatchingTextRendererProps {
  element: DragMatchingTextElement;
  isLiveMode?: boolean;
  selectedTextId?: string | undefined;
  isFrozen?: boolean | undefined;
}

export function DragMatchingTextRenderer({
  element,
  isLiveMode = false,
  selectedTextId,
  isFrozen,
}: DragMatchingTextRendererProps) {
  const dispatch = useAppDispatch();
  const { handleQuestionDragAndDrop } = useQuestionHandlers();
  const { handleFileDrop } = useLocalImageUpload((media, containerId) =>
    handleQuestionDragAndDrop(media, containerId)
  );

  const handleToggleIsCorrectAnswer = (e: React.MouseEvent, index: number) => {
    e.stopPropagation();

    if (index > element.textChoices.length - 1) return;

    const updatedTextChoices = element.textChoices.map((choice, i) => ({
      ...choice,
      isCorrectAnswer: i === index,
    }));

    dispatch(
      updateElementProps({
        id: element.id,
        props: {
          textChoices: updatedTextChoices,
        },
      })
    );
  };

  const handleTextChoiceChange = (text: string, index: number) => {
    if (index > element.textChoices.length - 1) return;

    const updatedTextChoices = element.textChoices.map((choice, i) => ({
      ...choice,
      text: index === i ? text : choice.text,
    }));

    dispatch(
      updateElementProps({
        id: element.id,
        props: {
          textChoices: updatedTextChoices,
        },
      })
    );
  };

  const handleAddChoice = () => {
    if (element.textChoices.length > 2) return;

    dispatch(
      updateElementProps({
        id: element.id,
        props: {
          textChoices: [...element.textChoices, { text: "Choice", isCorrectAnswer: false }],
        },
      })
    );
  };

  const handleDeleteImage = (event: React.MouseEvent) => {
    event.stopPropagation();

    dispatch(
      updateElementProps({
        id: element.id,
        props: {
          media: {
            id: null,
            src: null,
            filename: null,
          },
        },
      })
    );
  };

  return (
    <div
      style={{
        ...DragMatchingTextStyles.outerDiv,
        borderRadius: element.border.cornerRadius,
        backgroundColor: element.backgroundColor ?? "transparent",
        pointerEvents: isLiveMode ? "none" : "auto",
      }}
      className="text-black"
    >
      {!isLiveMode && element.textChoices.length < 3 && (
        <div onClick={handleAddChoice} className="absolute right-2 top-2 z-10">
          <IconButton
            aria-label="Add choice"
            variant="primary"
            shape="circle"
            size="sm"
            onClick={handleAddChoice}
          >
            <HiPlus className="w-5 h-5" aria-hidden="true" title="Add choice" />
          </IconButton>
        </div>
      )}

      {/* Image section */}
      {isLiveMode ? (
        <div style={DragMatchingTextStyles.imageContainer}>
          {element.media?.src ? (
            <img
              draggable={false}
              src={element.media.src}
              alt={element.media.filename || "matching image"}
              style={DragMatchingTextStyles.image}
            />
          ) : (
            <div className="flex flex-col items-center gap-2 w-full h-full">
              <div className="w-full h-full rounded-xl bg-slate-200 flex items-center justify-center">
                <AiFillPicture fontSize={60} />
              </div>
            </div>
          )}
        </div>
      ) : (
        <DroppableZone
          id={`${element.id}-drag-matching-image`}
          data={{
            id: `question//dragMatchingText//${element.id}`,
            type: "question-dragMatchingText",
          }}
          allowedType={["image"]}
          style={DragMatchingTextStyles.imageContainer}
          defaultBorderClass={element.media.src ? "" : "border-2 border-dashed border-slate-300"}
          onLocalFileDrop={(file) =>
            handleFileDrop(file, `question//dragMatchingText//${element.id}`)
          }
        >
          {element.media?.src ? (
            <div
              style={{ position: "relative", width: "100%", height: "100%" }}
              className="group/image"
            >
              <img
                draggable={false}
                src={element.media.src}
                alt={element.media.filename || "matching image"}
                style={DragMatchingTextStyles.image}
              />
              <DeleteImageButton onClick={handleDeleteImage} />
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <div className="w-16 h-16 bg-slate-200 flex items-center justify-center">
                <AiFillPicture className="text-4xl text-slate-400" />
              </div>
              <span className="text-xs text-slate-500 font-medium">Drop image here</span>
            </div>
          )}
        </DroppableZone>
      )}

      {/* Text Choices */}
      <div style={DragMatchingTextStyles.textChoicesContainer}>
        {element?.textChoices?.map((item, index) => {
          const graded = gradedFillStyle(
            gradeChoice(item.isCorrectAnswer, selectedTextId === `${index}`, isFrozen)
          );

          return (
            <div
              key={`${item.text}-${item.isCorrectAnswer}-${index}`}
              style={DragMatchingTextStyles.textChoice}
            >
              {!isLiveMode &&
                (item.isCorrectAnswer ? (
                  <MdCheckBox
                    onClick={(e) => handleToggleIsCorrectAnswer(e, index)}
                    className="absolute left-2 text-success-500 text-xl cursor-pointer transition-transform hover:scale-110 z-10"
                  />
                ) : (
                  <MdCheckBoxOutlineBlank
                    onClick={(e) => handleToggleIsCorrectAnswer(e, index)}
                    className="absolute left-2 text-slate-400 text-xl cursor-pointer transition-transform hover:scale-110 z-10"
                  />
                ))}
              <div style={{ ...DragMatchingTextStyles.textContainer, ...graded }}>
                <p
                  contentEditable={!isLiveMode}
                  data-no-moveable-drag
                  suppressContentEditableWarning
                  onMouseDown={(e) => {
                    if (!isLiveMode) e.currentTarget.focus();
                  }}
                  onBlur={(e) => handleTextChoiceChange(e.target.innerText, index)}
                  className="outline-none"
                  style={{ fontFamily: CANVAS_DEFAULT_FONT_FAMILY, color: graded.color }}
                >
                  {item.text}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
